import { addDays } from '@bb/shared/core';

/** Периоды статистики и подписи к ним. Все даты — 'YYYY-MM-DD' в поясе семьи. */
export type PeriodKind = 'day' | 'week' | 'month' | 'year';

export interface Period {
  kind: PeriodKind;
  from: string;
  to: string;
}

const MONTHS_NOM = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const MONTHS_GEN = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
const MONTHS_SHORT = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
export const MONTHS_TINY = ['Я', 'Ф', 'М', 'А', 'М', 'И', 'И', 'А', 'С', 'О', 'Н', 'Д'];
export const WEEKDAYS_SHORT = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const parts = (d: string) => d.split('-').map(Number) as [number, number, number];
const iso = (y: number, m: number, d: number) => new Date(Date.UTC(y, m - 1, d)).toISOString().slice(0, 10);

/** 0 = понедельник … 6 = воскресенье. */
export function weekday(d: string): number {
  const [y, m, day] = parts(d);
  return (new Date(Date.UTC(y, m - 1, day)).getUTCDay() + 6) % 7;
}

export function periodOf(kind: PeriodKind, anchor: string): Period {
  const [y, m] = parts(anchor);
  switch (kind) {
    case 'day':
      return { kind, from: anchor, to: anchor };
    case 'week':
      // Последние 7 дней, заканчивая выбранным днём: в любой день недели график полный.
      return { kind, from: addDays(anchor, -6), to: anchor };
    case 'month':
      return { kind, from: iso(y, m, 1), to: iso(y, m + 1, 0) };
    case 'year':
      return { kind, from: iso(y, 1, 1), to: iso(y, 12, 31) };
  }
}

/** Соседний период (−1 — предыдущий, +1 — следующий). */
export function shiftPeriod(p: Period, dir: -1 | 1): Period {
  const [y, m, d] = parts(p.from);
  switch (p.kind) {
    case 'day':
      return periodOf('day', addDays(p.from, dir));
    case 'week':
      return periodOf('week', addDays(p.to, 7 * dir));
    case 'month':
      return periodOf('month', iso(y, m + dir, 1));
    case 'year':
      return periodOf('year', iso(y + dir, 1, d));
  }
}

export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(to) - Date.parse(from)) / 86400000) + 1;
}

export function shortDate(d: string): string {
  const [, m, day] = parts(d);
  return `${day} ${MONTHS_SHORT[m - 1]}`;
}

export function longDate(d: string): string {
  const [, m, day] = parts(d);
  return `${day} ${MONTHS_GEN[m - 1]}`;
}

export function monthName(ym: string): string {
  const [y, m] = parts(`${ym}-01`);
  return `${MONTHS_NOM[m - 1]} ${y}`;
}

export function periodLabel(p: Period, today: string): string {
  const [y, m, d] = parts(p.from);
  const [ty] = parts(today);
  switch (p.kind) {
    case 'day': {
      if (p.from === today) return `Сегодня, ${longDate(p.from)}`;
      if (p.from === addDays(today, -1)) return `Вчера, ${longDate(p.from)}`;
      return `${WEEKDAYS_SHORT[weekday(p.from)]}, ${d} ${MONTHS_GEN[m - 1]}${y !== ty ? ` ${y}` : ''}`;
    }
    case 'week': {
      const [y2, m2, d2] = parts(p.to);
      const left = m === m2 ? `${d}` : `${d} ${MONTHS_SHORT[m - 1]}`;
      return `${left} – ${d2} ${MONTHS_SHORT[m2 - 1]}${y2 !== ty ? ` ${y2}` : ''}`;
    }
    case 'month':
      return y === ty ? MONTHS_NOM[m - 1]! : monthName(p.from.slice(0, 7));
    case 'year':
      return String(y);
  }
}

/** «+20 мин …» */
export const PREV_LABEL: Record<PeriodKind, string> = {
  day: 'к обычному дню',
  week: 'к прошлым 7 дням',
  month: 'к прошлому месяцу',
  year: 'к прошлому году',
};

/** Когда разницы почти нет. */
export const SAME_LABEL: Record<PeriodKind, string> = {
  day: 'как обычно',
  week: 'как в прошлые 7 дней',
  month: 'как в прошлом месяце',
  year: 'как в прошлом году',
};

/** Список месяцев 'YYYY-MM' периода. */
export function monthsOf(p: Period): string[] {
  const out: string[] = [];
  let [y, m] = parts(p.from);
  const [ty, tm] = parts(p.to);
  while (y < ty || (y === ty && m <= tm)) {
    out.push(`${y}-${String(m).padStart(2, '0')}`);
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return out;
}

export { MONTHS_SHORT };
